import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { AnalyticsPageVisit } from '../types/analytics';

export class AnalyticsTrackingService {
  private static sessionDocId: string | null = null;
  private static sessionId: string | null = null;
  private static siteId: string | null = null;
  private static sessionStartTime: Date | null = null;
  private static currentPageVisit: AnalyticsPageVisit | null = null;
  private static pageVisits: AnalyticsPageVisit[] = [];
  private static ctaClicks: number = 0;
  private static isInitialized: boolean = false;
  private static isStartingSession: boolean = false; // セッション開始中の重複防止フラグ
  private static firstVisitedPages: Set<string> = new Set(); // 初回訪問したページ（到達数カウント用）

  /**
   * セッションIDを生成
   */
  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  /**
   * デバイスタイプを判定
   */
  private static getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * メタデータを取得
   */
  private static getMetadata() {
    return {
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      deviceType: this.getDeviceType()
    };
  }

  /**
   * プレビューモードかどうかを判定
   */
  private static isPreviewMode(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hostname = window.location.hostname;
    return hostname === 'preview.qwipe.jp' || 
           hostname === 'www.preview.qwipe.jp' || 
           hostname.includes('preview.qwipe.jp');
  }

  /**
   * セッションを開始（ドキュメント作成）
   */
  static async startSession(siteId: string, firstPageId: string): Promise<void> {
    // プレビューモードの場合はトラッキングをスキップ（最優先チェック）
    if (this.isPreviewMode()) {
      return;
    }

    try {
      // 既にセッションが開始されている場合は何もしない
      if (this.isInitialized && this.sessionDocId) {
        return;
      }

      // セッション開始中の場合も何もしない（重複防止）
      if (this.isStartingSession) {
        return;
      }

      this.isStartingSession = true;
      
      this.sessionId = this.generateSessionId();
      this.sessionStartTime = new Date();
      this.siteId = siteId;
      this.pageVisits = [];
      this.ctaClicks = 0;
      this.isInitialized = true;
      this.firstVisitedPages = new Set(); // 初回訪問ページをリセット
      
      // セッション開始時点で1ページ目に到達したとみなす
      if (!firstPageId) {
        throw new Error('最初のページIDが指定されていません');
      }
      const initialPageId = firstPageId;
      
      // 初回訪問として記録
      this.firstVisitedPages.add(initialPageId);
      
      // 現在のページ訪問を1ページ目に設定
      this.currentPageVisit = {
        pageId: initialPageId,
        startTime: this.sessionStartTime
      };
      
      // Firestoreにセッションドキュメントを作成
      const docRef = await addDoc(collection(db, 'analytics_sessions'), {
        siteId,
        sessionId: this.sessionId,
        startTime: Timestamp.fromDate(this.sessionStartTime),
        endTime: null,
        pageVisits: [{
          pageId: initialPageId,
          startTime: this.sessionStartTime.getTime(), // ミリ秒で保存
          endTime: null
        }],
        pagesVisited: [initialPageId],
        firstVisitedPages: [initialPageId], // 初回訪問ページ（到達数カウント用）
        ctaClicks: 0,
        exitPageId: null,
        metadata: this.getMetadata()
      });

      this.sessionDocId = docRef.id;
      
      // アンロードハンドラを設定（1回のみ）
      this.setupUnloadHandler();
    } catch (error) {
    } finally {
      this.isStartingSession = false;
    }
  }

  /**
   * ページビューを記録
   */
  static async trackPageView(siteId: string, pageId: string): Promise<void> {
    // プレビューモードの場合はトラッキングをスキップ
    if (this.isPreviewMode()) {
      return;
    }

    try {
      if (!this.sessionDocId) {
        return;
      }

      // 初回訪問かどうかをチェック
      const isFirstVisit = !this.firstVisitedPages.has(pageId);
      if (isFirstVisit) {
        this.firstVisitedPages.add(pageId);
      }

      // 前のページの滞在時間を記録
      if (this.currentPageVisit) {
        const endTime = new Date();
        this.currentPageVisit.endTime = endTime;
        this.pageVisits.push({ ...this.currentPageVisit });
      }

      // 新しいページ訪問を開始
      this.currentPageVisit = {
        pageId,
        startTime: new Date()
      };

      // 最初のページ訪問の場合、即座にpageVisitsに追加
      if (this.pageVisits.length === 0) {
        this.pageVisits.push({ ...this.currentPageVisit });
      }

      // セッションドキュメントを更新
      await this.updateSession();
    } catch (error) {
    }
  }

  /**
   * CTAクリックを記録
   */
  static async trackCTAClick(siteId: string, pageId?: string): Promise<void> {
    // プレビューモードの場合はトラッキングをスキップ
    if (this.isPreviewMode()) {
      return;
    }

    try {
      if (!this.sessionDocId) {
        return;
      }

      this.ctaClicks++;
      
      // 現在のページにCTAクリックを記録
      if (this.currentPageVisit) {
        if (!this.currentPageVisit.ctaClicks) {
          this.currentPageVisit.ctaClicks = 0;
        }
        this.currentPageVisit.ctaClicks++;
      }
      
      await this.updateSession();
    } catch (error) {
    }
  }

  /**
   * セッションドキュメントを更新
   */
  private static async updateSession(): Promise<void> {
    // プレビューモードの場合は更新をスキップ
    if (this.isPreviewMode()) {
      return;
    }

    if (!this.sessionDocId || !this.siteId || !this.sessionStartTime) return;

    try {
      // 訪問したページのリスト（重複なし）
      const allPageIds = [
        ...this.pageVisits.map(v => v.pageId),
        ...(this.currentPageVisit ? [this.currentPageVisit.pageId] : [])
      ];
      const uniquePageIds = Array.from(new Set(allPageIds));

      // 現在のページIDを取得
      const currentPageId = this.currentPageVisit?.pageId || null;

      // 現在のページ訪問も含める
      const allPageVisits = [
        ...this.pageVisits,
        ...(this.currentPageVisit ? [{
          ...this.currentPageVisit,
          endTime: this.currentPageVisit.endTime || new Date()
        }] : [])
      ];

      await updateDoc(doc(db, 'analytics_sessions', this.sessionDocId), {
        pageVisits: allPageVisits.map(v => ({
          pageId: v.pageId,
          startTime: typeof v.startTime === 'number' ? v.startTime : v.startTime.getTime(), // ミリ秒で保存
          endTime: v.endTime ? (typeof v.endTime === 'number' ? v.endTime : v.endTime.getTime()) : null, // ミリ秒で保存
          ctaClicks: v.ctaClicks || 0
        })),
        pagesVisited: uniquePageIds,
        firstVisitedPages: Array.from(this.firstVisitedPages), // 初回訪問ページ（到達数カウント用）
        ctaClicks: this.ctaClicks,
        exitPageId: currentPageId
      });
    } catch (error) {
    }
  }

  /**
   * セッションを終了
   */
  static async endSession(): Promise<void> {
    // プレビューモードの場合は終了処理をスキップ
    if (this.isPreviewMode()) {
      return;
    }

    try {
      if (!this.sessionDocId || !this.sessionStartTime) return;

      const endTime = new Date();

      // 最後のページの滞在時間を記録
      if (this.currentPageVisit) {
        this.currentPageVisit.endTime = endTime;
        this.pageVisits.push({ ...this.currentPageVisit });
      }

      // 訪問したページのリスト
      const uniquePageIds = Array.from(new Set(this.pageVisits.map(v => v.pageId)));
      const exitPageId = this.currentPageVisit?.pageId || null;

      // 最終更新
      await updateDoc(doc(db, 'analytics_sessions', this.sessionDocId), {
        endTime: Timestamp.fromDate(endTime),
        pageVisits: this.pageVisits.map(v => ({
          pageId: v.pageId,
          startTime: typeof v.startTime === 'number' ? v.startTime : v.startTime.getTime(), // ミリ秒で保存
          endTime: v.endTime ? (typeof v.endTime === 'number' ? v.endTime : v.endTime.getTime()) : null, // ミリ秒で保存
          ctaClicks: v.ctaClicks || 0
        })),
        pagesVisited: uniquePageIds,
        firstVisitedPages: Array.from(this.firstVisitedPages), // 初回訪問ページ（到達数カウント用）
        ctaClicks: this.ctaClicks,
        exitPageId
      });

      // リセット
      this.sessionDocId = null;
      this.sessionId = null;
      this.siteId = null;
      this.sessionStartTime = null;
      this.currentPageVisit = null;
      this.pageVisits = [];
      this.ctaClicks = 0;
      this.isInitialized = false;
      this.firstVisitedPages = new Set();
    } catch (error) {
    }
  }

  /**
   * ページアンロード時の処理
   */
  private static setupUnloadHandler(): void {
    // 既に設定済みの場合はスキップ
    if ((window as any).__analyticsUnloadHandlerSet) {
      return;
    }
    (window as any).__analyticsUnloadHandlerSet = true;

    // ページ離脱時にセッションを終了
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });

    // ページ非表示時にもセッションを終了
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.endSession();
      }
    });
  }
}
